import {
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentHistory,
  ContentType,
  ContentWorkflow,
  ReviewRequestStatus,
  WorkflowState,
  InlineComment,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React, { type JSX } from 'react';
import join from 'join-array';
import { autoGenerateHref } from '../../api/ContentUtil';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { displayAuthorInfo } from '../../api/UtilsApi';
import { isUUID } from 'class-validator';
import constants from '../../common/constants';
import AppContextProps from '../../contexts/AppContextProps';
import '../../resources/styles/scss/components/pages/content/EditContentPage.css';
import Breadcrumbs from '../common/Breadcrumbs';
import LoadingPage from '../common/LoadingPage';
import NotFound from '../common/NotFound';
import DraftViewerRole from '../utils/DraftViewerRole';
import { AuthorFinalizingInfo } from './AuthorFinalizingInfo';
import DraftContentData from './content/DraftContentData';
import WorkflowLockInfo from './WorkflowLockInfo';
import RouterPaths from '../../common/RouterPaths';
import BrowserHistory from '../utils/BrowserHistory';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import LocalStorage from '../../common/LocalStorage';
import { APGLEntityEntry } from '@amzn/awscat-editor/dist/extensions/apgl-entity';
import { loadEntities } from '../utils/EntitiesUtils';
import UnAuthorized from '../common/UnAuthorized';
import { convertEntities } from '@amzn/awscat-editor';
import { Box, Grid, GridProps } from '@amzn/awsui-components-core';

// Props injected by withRouter (location, match) and withAppContext (appContext)
interface ContentViewerProps extends AppContextProps {
  location: any;
  match: any;
}

// All state needed to display a content draft: the content itself, its workflow,
// collaborators, review status, inline comments, and various UI flags.
interface ContentViewerState {
  isLoadingData: boolean;
  coAuthors?: ContentAccessUser[];
  peerReviewers?: ContentAccessUser[];
  content?: Content;
  contentLastModified?: DateTime;
  contentAudience?: ContentAudience;
  workflow?: ContentWorkflow;
  workflowEvents?: any[];
  contentId?: string;
  validationError?: any;
  isFileUploading: boolean;
  workflowHistory?: ContentHistory[];
  attachmentsReadOnly: boolean;
  submittingForReview?: boolean;
  reviewRequestStatus?: ReviewRequestStatus;
  isPeerReviewer?: boolean;
  pendingPeerReviewers?: ContentAccessUser[];
  acceptedPeerReviewers?: ContentAccessUser[];
  inlineComments?: InlineComment[];
  updatedInlineComments?: InlineComment[];
  showInlineCommentsInEditMode?: boolean;
  showInlineCommentsInReviewerPage?: boolean;
  isCommentingModeEnabled?: boolean;
  setIsCommentMpdeEnabled?: boolean; // NOTE: typo in original — "Mpde" should be "Mode"
  showInlineCommentsInPublisherPage?: boolean;
  entities: { [key: string]: APGLEntityEntry };
}

/**
 * BASE CLASS for all content viewer pages (Author, Reviewer, Publisher, BarRaiser, Admin).
 *
 * Uses the TEMPLATE METHOD pattern: this class owns the render() lifecycle and data-loading,
 * while subclasses customize behavior by overriding protected "hook" methods/properties:
 *   - canDisplayView()        → access control (who can see this page)
 *   - renderControls()        → bottom action buttons (submit, approve, reject, etc.)
 *   - renderActions()         → status/action section shown above controls
 *   - showReviewFeedback()    → whether to fetch & display review events
 *   - showContentControls()   → whether to show the edit toolbar on the content
 *   - getBreadcrumbItems()    → custom breadcrumb trail
 *   - isEditingAllowed        → can the user enter edit mode?
 *   - draftViewerRole         → which role perspective to render (Author, Reviewer, etc.)
 *   - showPublicationHistory  → fetch and display version history?
 *   - showSectionalComments   → show per-section comment UI?
 *   - showLastBarRaiserEvaluation → show the bar raiser quality score?
 *   - showPeerReviewInfo      → show peer review status messages?
 */
class ContentViewer extends React.Component<ContentViewerProps, ContentViewerState> {
  // ─── TEMPLATE METHOD HOOKS (overridden by subclasses) ───────────────────────

  // Whether the user can toggle into edit mode on this page
  protected isEditingAllowed: boolean = false;
  // Whether to show the content abstract/summary section
  protected showContentAbstract: boolean = false;
  // Whether to fetch and display publication version history
  protected showPublicationHistory: boolean = true;
  // Whether to show tech-writer-specific metadata fields
  protected showTechwriterMetadata: boolean = false;
  // Whether to render per-section inline comment UI
  protected showSectionalComments: boolean = false;
  // Which role perspective this viewer renders as (Author, Reviewer, Publisher, Admin)
  // Affects lock-info display and edit permissions in child components
  // @ts-expect-error TS2322 - strict migration
  protected draftViewerRole: DraftViewerRole = null;
  // Whether this is the admin-specific view (affects some child component behavior)
  protected isAdminView: boolean = false;
  // Whether to show the last bar-raiser quality evaluation score
  protected showLastBarRaiserEvaluation: boolean = false;
  // Whether to show peer review acceptance info and flash messages
  protected showPeerReviewInfo: boolean = false;
  // Whether to show code validation errors in the editor
  protected showCodeValidationError: boolean = true;

  // ─── SERVICES ───────────────────────────────────────────────────────────────

  // Backend API client — used by this class and subclasses to fetch content, workflows, etc.
  protected readonly mplService: IMplServiceClient;
  // Pushes transient notification messages to the top-of-page flashbar
  protected flashbarMessageBroker: FlashbarMessageBroker;
  // Interval handle for polling attachment upload status (cleared on unmount)
  private attachmentsPolling: any;

  protected constructor(props: ContentViewerProps) {
    super(props);
    this.mplService = MplServiceClientFactory.create();
    this.flashbarMessageBroker = FlashbarMessageBroker.create();
    this.state = {
      attachmentsReadOnly: true,
      isFileUploading: false,
      isLoadingData: false,
      showInlineCommentsInReviewerPage: false,
      isCommentingModeEnabled: false,
      showInlineCommentsInPublisherPage: false,
      entities: {},
    };
  }

  // ─── REACT LIFECYCLE ────────────────────────────────────────────────────────

  // On mount: fetch all data for this content and scroll to top
  public componentDidMount() {
    this.reloadData();
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  }

  // On URL change (e.g. navigating between different content items): re-fetch data
  public componentDidUpdate(prevProps: ContentViewerProps) {
    const contentId = decodeURIComponent(this.props.match.params.contentId);
    const prevContentId = decodeURIComponent(prevProps.match.params.contentId);
    if (contentId !== prevContentId) {
      this.reloadData();
    }
  }

  // Cleanup: stop polling for attachment status when leaving the page
  public componentWillUnmount() {
    if (this.attachmentsPolling) {
      clearInterval(this.attachmentsPolling);
    }
  }

  // ─── RENDER (the "template method" — assembles the page from hook results) ──

  public render() {
    const { authnInfo, entities } = this.props.appContext;
    const {
      coAuthors,
      peerReviewers,
      content,
      contentAudience,
      workflowEvents,
      workflow,
      contentId,
      isLoadingData,
      workflowHistory,
      validationError,
      submittingForReview,
      contentLastModified,
      reviewRequestStatus,
      inlineComments,
      showInlineCommentsInReviewerPage,
      isCommentingModeEnabled,
      showInlineCommentsInPublisherPage,
    } = this.state;

    // Show spinner while data is loading
    if (isLoadingData) {
      return <LoadingPage />;
    }

    // If contentId couldn't be resolved (invalid UUID or fetch failed), show 404
    if (!contentId) {
      return <NotFound />;
    }

    // Build breadcrumb trail: app root → subclass-specific crumbs → current content name
    const breadcrumbItems = [
      {
        href: '/',
        text: constants.APP_NAME_BREAD_CRUMB,
      },
      ...this.getBreadcrumbItems(),
      {
        href: '',
        text:
          // @ts-expect-error TS18048 - strict migration
          convertEntities(content.contentMetadata.contentShortName, entities) ||
          // @ts-expect-error TS18048 - strict migration
          convertEntities(content.contentMetadata.contentName, entities),
      },
    ];

    // Editing is enabled only when the current user holds the lock on this content
    const isEditingEnabled =
      this.isEditingAllowed &&
      !!workflow?.lock &&
      workflow.lock.acquiredBy === authnInfo?.userProfile.userId;

    // In read-only mode, use a 3-row grid; in edit mode, single full-width column
    const gridDefinition: GridProps.ElementDefinition[] = !isEditingEnabled
      ? [{ colspan: 12 }, { colspan: 12 }, { colspan: 12 }]
      : [{ colspan: 12 }];

    // Hide breadcrumbs when in edit mode (full-screen editing experience)
    const breadcrumb = !isEditingEnabled ? <Breadcrumbs items={breadcrumbItems} /> : null;

    // Show a warning banner if another user currently holds the edit lock
    const workflowLockInfo = this.getWorflowLockInfo();
    if (workflowLockInfo) {
      gridDefinition.push({ colspan: 12 });
    }

    // Show info banner when content is being finalized for publication by someone
    const finalizingInProgressInfo =
      workflow?.publicationState === WorkflowState.FINALIZING ? (
        <AuthorFinalizingInfo contentId={contentId} workflowId={workflow.publicationWorkflowId} />
      ) : null;

    if (finalizingInProgressInfo) {
      gridDefinition.push({ colspan: 12 });
    }

    // Subclass-provided action controls (submit/approve/reject buttons) — hidden in edit mode
    const controls = !isEditingEnabled ? this.renderControls() : null;

    // Access control gate: if the current user shouldn't see this view, show unauthorized page
    if (!this.canDisplayView()) {
      return <UnAuthorized />;
    }

    // ─── PAGE LAYOUT ────────────────────────────────────────────────────────
    // Assembles: breadcrumb → lock warning → finalizing info → content body → controls
    // DraftContentData is the main content renderer — it switches between readonly and edit views
    // based on whether the current user holds the lock.
    const data = (
      <div>
        {breadcrumb}
        {workflowLockInfo}
        {finalizingInProgressInfo}
        <Grid gridDefinition={[{ colspan: 12 }]}>
          <DraftContentData
            contentId={contentId}
            workflowId={workflow?.draftWorkflowId}
            coAuthors={coAuthors}
            contentOwner={workflow?.ownerProfile}
            peerReviewers={peerReviewers}
            content={content}
            contentLastModified={contentLastModified}
            contentAudience={contentAudience}
            isEditingAllowed={this.isEditingAllowed}
            showContentControls={this.showContentControls()}
            workflow={workflow}
            workflowEvents={workflowEvents}
            showReviewFeedback={this.showReviewFeedback(workflow)}
            onEnableEditMode={this.editModeEnabled}
            onDisableEditMode={this.editModeDisabled}
            onContentDataChanged={this.updateContent}
            onContentAudienceChanged={this.updateContentAudience}
            workflowHistory={workflowHistory}
            showTechwriterMetadata={this.showTechwriterMetadata}
            showSectionalComments={this.showSectionalComments}
            validationError={validationError}
            onSubmitForReview={this.handleSubmitForReviewInEditMode}
            onSubmitForReviewComplete={this.handleSubmitForReviewComplete}
            draftViewerRole={this.draftViewerRole}
            submittingForReview={submittingForReview}
            onReviewComplete={this.navigateBack}
            isAdminView={this.isAdminView}
            actionForm={this.renderActions()}
            publicationState={workflow?.publicationState}
            reviewRequestStatus={reviewRequestStatus}
            showLastBarRaiserEvaluation={this.showLastBarRaiserEvaluation}
            inlineComments={inlineComments}
            updateInlineCommentsLocation={this.updateInlineCommentsLocation}
            updatedInlineComments={this.state.updatedInlineComments}
            setInlineCommentsData={this.setInlineCommentsData}
            showInlineCommentsInEditMode={this.state.showInlineCommentsInEditMode}
            setShowInlineCommentsInEditMode={this.setShowInlineCommentsInEditMode}
            showInlineCommentsInReviewerPage={showInlineCommentsInReviewerPage}
            setShowInlineCommentsInReviewerPage={this.setShowInlineCommentsInReviewerPage}
            isCommentingModeEnabled={isCommentingModeEnabled}
            enableCommentMode={this.enableCommentMode}
            showInlineCommentsInPublisherPage={showInlineCommentsInPublisherPage}
            setShowInlineCommentsInPublisherPage={this.setShowInlineCommentsInPublisherPage}
            reFetchInlineComments={this.reFetchInlineComments}
            entities={this.props.appContext.entities}
            showCodeValidationError={this.showCodeValidationError}
          />
        </Grid>
        {controls}
        {/* Lock info and finalizing info repeated at bottom so user sees them after scrolling */}
        {workflowLockInfo}
        {finalizingInProgressInfo}
      </div>
    );

    // Read-only mode: white background with padding; Edit mode: full-width no-chrome layout
    return !isEditingEnabled ? (
      <div className="white-background">
        <Box padding="m">
          <Grid gridDefinition={gridDefinition}>{data}</Grid>
        </Box>
      </div>
    ) : (
      <div id="edit-content">{data}</div>
    );
  }

  // ─── STATE SETTERS (passed down to child components as callbacks) ───────────

  public setShowInlineCommentsInReviewerPage = (show: boolean) => {
    this.setState({ showInlineCommentsInReviewerPage: show });
  };

  public enableCommentMode = (enabled: boolean) => {
    this.setState({ isCommentingModeEnabled: enabled });
  };

  public setShowInlineCommentsInPublisherPage = (show: boolean) => {
    this.setState({ showInlineCommentsInPublisherPage: show });
  };

  // ─── TEMPLATE METHOD HOOKS (default implementations — subclasses override) ──

  // Access control: determines if the current user is allowed to see this view.
  // Default: everyone can see. AuthorContentViewer restricts to author/admin only.
  protected canDisplayView(): boolean {
    return true;
  }

  // Renders the bottom action controls (submit, approve, reject buttons, etc.)
  // Each subclass provides its own control component.
  protected renderControls(): JSX.Element | null | false {
    return null;
  }

  // Subclass-specific breadcrumb items inserted between app root and content name.
  // e.g. BarRaiserContentViewer adds "Bar Raiser Dashboard".
  protected getBreadcrumbItems(): Array<{ text: string; href: string }> {
    return [];
  }

  // Called when content is modified in the editor — auto-generates anchor hrefs
  // for content sections, then updates state so the UI re-renders.
  protected updateContent = async (content: Content) => {
    autoGenerateHref(content.contentData, content.contentMetadata);
    this.setState({
      content,
    });
  };

  // Called when the content audience (internal/external) changes in the editor
  protected updateContentAudience = async (contentAudience: ContentAudience) => {
    this.setState({
      contentAudience,
    });
  };

  // Renders a status/action section (e.g. "Review pending by X", or a "Show All Comments" button).
  // Subclasses override to show role-specific status info.
  protected renderActions = () => {
    return null;
  };

  // Whether to show the edit toolbar/controls on the content body.
  // AuthorContentViewer overrides this based on workflow state.
  protected showContentControls(): boolean {
    return false;
  }

  // Whether to fetch and display review feedback events (reviewer comments, approvals, etc.)
  // AuthorContentViewer hides this for early workflow states; BarRaiser always shows it.
  protected showReviewFeedback(workflow: ContentWorkflow | undefined): boolean {
    return false;
  }

  // Default navigation after an action completes — go to home page.
  protected navigateBack = async () => {
    BrowserHistory.push(`${RouterPaths.HOME_PAGE}`);
  };

  // Called when user clicks "Submit for Review" while in edit mode.
  // Scrolls to top and shows a loading state on the submit button.
  protected handleSubmitForReviewInEditMode = () => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
    this.setState({
      submittingForReview: true,
    });
  };

  // Called when the submit-for-review API call completes (success or failure).
  // Clears the loading state.
  protected handleSubmitForReviewComplete = () => {
    this.setState({
      submittingForReview: false,
    });
  };

  // Formats a list of peer reviewers into a human-readable string like
  // "Alice (Org1), Bob (Org2) and (3 more...)" using the join-array library.
  protected displayJoinPeerReviewers = (reviewers: ContentAccessUser[]) => {
    return join({
      array: reviewers,
      separator: ', ',
      last: ' and ',
      max: 2,
      // @ts-expect-error TS7006 - strict migration
      maxMessage: (missed) => `(${missed} more...)`,
      // @ts-expect-error TS7006 - strict migration
      each: (pr) => displayAuthorInfo(pr.userName, pr.organizationName),
    });
  };

  // ─── PRIVATE METHODS ────────────────────────────────────────────────────────

  // Callback when user enters edit mode — unlock attachments and refresh data
  private editModeEnabled = async () => {
    this.setState({
      attachmentsReadOnly: false,
    });
    this.reloadData();
  };

  // Shows a warning banner when another user holds the edit lock.
  // Skipped for Reviewer role (reviewers don't need to edit, so the lock doesn't matter to them).
  // Resolves the lock holder's display name from owner profile or co-authors list.
  private getWorflowLockInfo = () => {
    const { authnInfo } = this.props.appContext;
    const { workflow, coAuthors } = this.state;
    const acquiredBy = workflow?.lock?.acquiredBy;
    if (
      this.draftViewerRole !== DraftViewerRole.Reviewer &&
      acquiredBy &&
      acquiredBy !== authnInfo?.userProfile.userId
    ) {
      const { ownerProfile } = workflow;
      let lockedByUserName = '';
      if (acquiredBy === ownerProfile?.userId) {
        lockedByUserName = displayAuthorInfo(ownerProfile.name, ownerProfile.organizationName);
      } else {
        const lockedByCoAuthor = coAuthors?.find((author) => author?.userId === acquiredBy);
        lockedByUserName = displayAuthorInfo(
          lockedByCoAuthor?.userName ?? '',
          lockedByCoAuthor?.organizationName,
        );
      }
      return <WorkflowLockInfo userName={lockedByUserName} />;
    }
    return null;
  };

  // Callback when user exits edit mode — lock attachments back to readonly and refresh
  private editModeDisabled = async () => {
    this.setState({
      attachmentsReadOnly: true,
    });
    await this.reloadData();
  };

  // Persists updated inline comment positions to localStorage after the editor
  // re-flows content (e.g. after an edit that shifts section boundaries).
  private updateInlineCommentsLocation = (comments: InlineComment[], sectionId: string) => {
    LocalStorage.setUpdatedComments(this.props.match.params.contentId, comments, sectionId);
  };

  // Merges newly-fetched inline comments with existing ones in state.
  // Deduplicates by commentId — new comments take precedence over stale ones.
  private setInlineCommentsData = (comments: InlineComment[]) => {
    const commentsIdSet = new Set(comments.map((comment) => comment.commentId));
    const savedComments = [...comments];
    this.state.inlineComments?.forEach((comment) => {
      if (!commentsIdSet.has(comment.commentId)) {
        savedComments.push(comment);
      }
    });
    this.setState({
      inlineComments: savedComments,
    });
  };

  private setShowInlineCommentsInEditMode = (show: boolean) => {
    this.setState({ showInlineCommentsInEditMode: show });
  };

  // Re-fetches inline comments from the backend (e.g. after adding/resolving a comment)
  private reFetchInlineComments = async () => {
    const contentId = decodeURIComponent(this.props.match.params.contentId);
    const { workflow } = this.state;
    const { inlineComments } = await this.mplService.listInlineComments(
      contentId,
      // @ts-expect-error TS18048 - strict migration
      workflow.draftWorkflowId,
    );
    this.setState({
      inlineComments,
    });
  };

  /**
   * MAIN DATA LOADER — fetches everything needed to render the content viewer page.
   *
   * Sequence:
   * 1. Validate contentId is a UUID
   * 2. Load entity definitions (for rendering entity references in content)
   * 3. Fetch content + workflow from backend, plus the last quality evaluation score
   * 4. If content is a "Post" type, redirect to the post-specific viewer
   * 5. Auto-generate anchor hrefs for content sections
   * 6. In parallel, fetch: review events, publication history, co-authors, peer reviewers
   * 7. Categorize peer reviewers into accepted vs pending
   * 8. Fetch inline comments
   * 9. If all reviewers approved and this is the author view, show a flash message
   *    telling the author that an AWS user still needs to approve
   */
  private reloadData = async () => {
    const { appContext } = this.props;
    const userId = appContext?.authnInfo?.userProfile?.userId;
    const contentId = decodeURIComponent(this.props.match.params.contentId);

    // Guard: don't fetch if the URL param isn't a valid UUID
    if (!isUUID(contentId)) {
      return;
    }

    this.setState({ isLoadingData: true });

    try {
      // Step 1: Load entity definitions (AWS services, tools, etc.) for rendering
      const entities = await loadEntities();

      // Step 2: Fetch the content document and its workflow state
      const { content, createdAt, workflow } = await this.mplService.getContent(contentId);
      // Attach the quality score to the workflow object for downstream use
      const qualityEval = await this.mplService.getLastContentQualityEvaluation(contentId);
      workflow.lastQualityScore = qualityEval.responses.length > 0 ? qualityEval.score : undefined;

      // Step 3: Posts have their own viewer — redirect if this content is a Post
      if (content.contentMetadata.contentType === ContentType.Post) {
        BrowserHistory.push(`/post-viewer/${contentId}`);
        return;
      }

      // Step 4: Generate anchor IDs/hrefs for each content section (for in-page navigation)
      autoGenerateHref(content.contentData, content.contentMetadata);

      this.setState({
        content,
        contentLastModified: createdAt,
        workflow,
        // @ts-expect-error TS2322 - strict migration
        entities,
      });

      // Step 5: Fetch secondary data in parallel for performance
      // - Review events: only if this view shows review feedback (depends on subclass + state)
      // - Workflow history: only if this view shows publication history
      // - Co-authors and peer reviewers: always needed
      const [
        { events },
        { workflows },
        { accessUsers: coAuthors },
        { accessUsers: peerReviewers },
      ] = await Promise.all([
        // Check the fetched workflow, not this.state: the setState above is batched and not yet applied.
        this.showReviewFeedback(workflow)
          ? this.mplService.getContentEvents(contentId, workflow.draftWorkflowId)
          : { events: [] },
        this.showPublicationHistory
          ? this.mplService.listWorkflowHistory(contentId)
          : { workflows: [] },
        this.mplService.listContentSharedWith(contentId),
        this.mplService.listContentReviewers(contentId),
      ]);

      // Step 6: Categorize peer reviewers by their review request status
      const acceptedPeerReviewers = peerReviewers.filter(
        (pr) =>
          pr.reviewRequestStatus &&
          [
            ReviewRequestStatus.ACCEPTED,
            ReviewRequestStatus.APPROVED,
            ReviewRequestStatus.DISAPPROVED,
          ].includes(pr.reviewRequestStatus),
      );

      const pendingPeerReviewers = peerReviewers.filter(
        (pr) =>
          pr.reviewRequestStatus &&
          [ReviewRequestStatus.PENDING_ACCEPTANCE].includes(pr.reviewRequestStatus),
      );

      // Step 7: Determine if the current user is a peer reviewer and their status
      const isPeerReviewer = peerReviewers?.some((pr) => pr.userId === userId);
      let reviewRequestStatus: ReviewRequestStatus;
      if (isPeerReviewer) {
        // @ts-expect-error TS7006 - strict migration
        reviewRequestStatus = peerReviewers.find((pr) => pr.userId === userId)
          .reviewRequestStatus as ReviewRequestStatus;
      }

      // Step 8: Fetch inline comments for the current draft workflow
      const { inlineComments } = await this.mplService.listInlineComments(
        contentId,
        workflow.draftWorkflowId,
      );

      this.setState({
        coAuthors,
        peerReviewers,
        contentAudience: workflow.contentAudience,
        contentId,
        // @ts-expect-error TS2454 - strict migration
        reviewRequestStatus,
        isPeerReviewer,
        pendingPeerReviewers,
        acceptedPeerReviewers,
        workflowEvents: events,
        workflowHistory: workflows,
        inlineComments,
      });

      // Step 9: If all peer reviewers approved but the content is still IN_REVIEW,
      // it means an AWS internal user still needs to approve. Show a flash message
      // to the author so they know what's blocking progress.
      const allReviewersApproved = peerReviewers?.every(
        (pr) => pr.reviewRequestStatus && pr.reviewRequestStatus === ReviewRequestStatus.APPROVED,
      );

      if (
        workflow?.draftState === WorkflowState.IN_REVIEW &&
        allReviewersApproved &&
        this.showPeerReviewInfo
      ) {
        this.flashbarMessageBroker.pushInfoMessage(
          `Content needs an AWS user to review and approve to move from peer review state.`,
          15000,
        );
      }
    } finally {
      this.setState({ isLoadingData: false });
    }
  };
}

export default ContentViewer;
